/**
 * admin/AdminProducts.js — what's on the shelf.
 *
 * A product is edited whole: its details, its variants and its photos all
 * save together on one button. Variants are the buyable combinations —
 * a colour, a size, or both — and each carries its own stock.
 *
 * Stock left blank means "not counted", which is different from zero:
 * blank sells freely, zero reads as sold out.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  HiOutlineShoppingBag, HiOutlinePlus, HiOutlineTrash, HiOutlineXMark,
  HiOutlinePencilSquare, HiOutlinePhoto,
} from "react-icons/hi2";
import toast from "react-hot-toast";

import AdminLayout from "./AdminLayout";
import ImagePicker from "./ImagePicker";
import { productsAPI, mediaUrl } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import "./AdminProducts.css";

const STATUSES = [
  { v: "draft", label: "Draft", hint: "Only you can see it" },
  { v: "published", label: "Published", hint: "On sale" },
  { v: "archived", label: "Archived", hint: "Off sale, orders kept" },
];

const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

const BLANK = {
  name: "", slug: "", summary: "", overview: "", description: "", details: "",
  price: 0, status: "draft", sort_order: 0, variants: [], images: [],
};

const BLANK_VARIANT = { colour: "", colour_hex: "#a65d2b", size: "", sku: "", price: "", stock: "", active: true };

/* ══════════════════════════════════════════════════════════
   Editor
══════════════════════════════════════════════════════════ */
function ProductEditor({ product, onClose, onSaved }) {
  const [f, setF] = useState(() => (product ? { ...BLANK, ...product } : BLANK));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => {
    setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
    setError("");
  };

  const setVariant = (i, key, value) =>
    setF((x) => ({ ...x, variants: x.variants.map((v, n) => (n === i ? { ...v, [key]: value } : v)) }));

  const addVariant = () => setF((x) => ({ ...x, variants: [...x.variants, { ...BLANK_VARIANT }] }));
  const dropVariant = (i) => setF((x) => ({ ...x, variants: x.variants.filter((_, n) => n !== i) }));

  const addImage = (path) => {
    if (!path) return;
    setF((x) => (x.images.some((i) => i.path === path) ? x : { ...x, images: [...x.images, { path, alt: "" }] }));
  };
  const dropImage = (i) => setF((x) => ({ ...x, images: x.images.filter((_, n) => n !== i) }));

  const save = async () => {
    if (!f.name.trim()) { setError("Give the product a name."); return; }
    if (f.variants.length === 0) { setError("Add at least one variant — that's what people actually buy."); return; }

    setSaving(true); setError("");
    try {
      const payload = { ...f, price: Number(f.price) || 0, sort_order: parseInt(f.sort_order, 10) || 0 };
      if (product) await productsAPI.update(product.id, payload);
      else await productsAPI.create(payload);
      toast.success(product ? "Saved." : "Product created.");
      onSaved();
    } catch (err) {
      setError(err.message || "Couldn't save that.");
    } finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal apr__modal" onClick={(e) => e.stopPropagation()}>
        <div className="aap__contact-head">
          <h3>{product ? "Edit product" : "New product"}</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark /></button>
        </div>

        <div className="ars__field">
          <label>Name</label>
          <input className="arb__form-input" value={f.name} onChange={set("name")} placeholder="e.g. Makua Tote Bag" />
        </div>

        <div className="apr__row">
          <div className="ars__field">
            <label>Price</label>
            <input className="arb__form-input" type="number" min={0} step="0.01" value={f.price} onChange={set("price")} />
            <span className="ars__optional">A variant can override this.</span>
          </div>
          <div className="ars__field">
            <label>Display order</label>
            <input className="arb__form-input" type="number" value={f.sort_order} onChange={set("sort_order")} />
          </div>
        </div>

        <div className="ars__field">
          <label>Line on the card</label>
          <input className="arb__form-input" value={f.summary} maxLength={400} onChange={set("summary")}
            placeholder="One sentence — this is what people read first." />
        </div>

        <div className="ars__field">
          <label>Overview <span className="ars__optional">(beside the photo)</span></label>
          <textarea className="arb__form-input" rows={3} value={f.overview} onChange={set("overview")} />
        </div>

        <div className="apr__row">
          <div className="ars__field">
            <label>Description <span className="ars__optional">(first accordion)</span></label>
            <textarea className="arb__form-input" rows={3} value={f.description} onChange={set("description")} />
          </div>
          <div className="ars__field">
            <label>Additional information <span className="ars__optional">(second accordion)</span></label>
            <textarea className="arb__form-input" rows={3} value={f.details} onChange={set("details")}
              placeholder={"Materials: …\nDimensions: …\nCare: …"} />
            <span className="ars__optional">One per line.</span>
          </div>
        </div>

        {/* ── Variants ── */}
        <div className="apr__section">
          <div className="apr__section-head">
            <h4>Variants</h4>
            <button type="button" className="a-btn a-btn--outline apr__mini" onClick={addVariant}>
              <HiOutlinePlus /> Add variant
            </button>
          </div>
          <p className="apr__hint">
            What someone actually buys. Leave stock blank if you're not counting;
            set it to 0 to show as sold out.
          </p>

          {f.variants.length === 0 ? (
            <p className="apr__empty">No variants yet — add at least one.</p>
          ) : (
            <div className="apr__variants">
              {f.variants.map((v, i) => (
                <div className="apr__variant" key={v.id || `new-${i}`}>
                  <input className="arb__form-input" placeholder="Colour" value={v.colour || ""}
                    onChange={(e) => setVariant(i, "colour", e.target.value)} />
                  <input className="apr__colour" type="color" value={v.colour_hex || "#a65d2b"}
                    title="Swatch shown on the product page"
                    onChange={(e) => setVariant(i, "colour_hex", e.target.value)} />
                  <input className="arb__form-input" placeholder="Size" value={v.size || ""}
                    onChange={(e) => setVariant(i, "size", e.target.value)} />
                  <input className="arb__form-input" placeholder="SKU" value={v.sku || ""}
                    onChange={(e) => setVariant(i, "sku", e.target.value)} />
                  <input className="arb__form-input" type="number" min={0} step="0.01" placeholder="Price"
                    value={v.price ?? ""} onChange={(e) => setVariant(i, "price", e.target.value)} />
                  <input className="arb__form-input" type="number" min={0} placeholder="Stock"
                    value={v.stock ?? ""} onChange={(e) => setVariant(i, "stock", e.target.value)} />
                  <button type="button" className="awk__icon-btn awk__icon-btn--danger"
                    title="Remove" onClick={() => dropVariant(i)}>
                    <HiOutlineTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Photos ── */}
        <div className="apr__section">
          <div className="apr__section-head">
            <h4>Photos</h4>
          </div>
          {f.images.length > 0 && (
            <div className="apr__images">
              {f.images.map((im, i) => (
                <div className="apr__image" key={im.path + i}>
                  <img src={mediaUrl(im.path)} alt="" />
                  {i === 0 && <span className="apr__image-main">MAIN</span>}
                  <button type="button" onClick={() => dropImage(i)} aria-label="Remove photo">
                    <HiOutlineXMark />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="apr__hint">The first one is used on the card and in the basket.</p>
          <ImagePicker label="Add a photo" value="" onChange={addImage} />
        </div>

        <div className="ars__field">
          <label>Status</label>
          <div className="ars__type-row">
            {STATUSES.map((s) => (
              <button key={s.v} type="button" title={s.hint}
                className={`ars__type-btn ${f.status === s.v ? "ars__type-btn--active" : ""}`}
                onClick={() => setF((x) => ({ ...x, status: s.v }))}>{s.label}</button>
            ))}
          </div>
          <span className="ars__optional">{STATUSES.find((s) => s.v === f.status)?.hint}</span>
        </div>

        {product && (
          <div className="ars__field">
            <label>Web address <span className="ars__optional">(optional)</span></label>
            <input className="arb__form-input" value={f.slug || ""} onChange={set("slug")} />
            <span className="ars__optional">Leave it alone unless you need to change the link.</span>
          </div>
        )}

        {error && <p className="awk__error">{error}</p>}

        <div className="awk__actions">
          <button type="button" className="a-btn a-btn--rust" onClick={save} disabled={saving}>
            {saving ? "Saving…" : product ? "Save changes" : "Create product"}
          </button>
          <button type="button" className="a-btn a-btn--outline" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════════════ */
export default function AdminProducts() {
  const { admin } = useCurrentAdmin();
  const canEdit = hasPermission(admin, "shop.edit");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsAPI.list(status ? { status } : {});
      setItems(res.data || []);
    } catch (err) { toast.error(err.message || "Couldn't load products."); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? items.filter((p) => p.name.toLowerCase().includes(n)) : items;
  }, [items, q]);

  const stats = useMemo(() => ({
    live: items.filter((p) => p.status === "published").length,
    sold: items.reduce((n, p) => n + p.sold, 0),
    low: items.filter((p) => p.stock != null && p.stock > 0 && p.stock <= 5).length,
  }), [items]);

  const remove = (p) => {
    toast((t) => (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <p style={{ margin: 0, fontWeight: 700 }}>Delete “{p.name}”?</p>
        <p style={{ margin: 0, fontSize: "0.78rem", color: "#6b6258" }}>This can't be undone.</p>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button onClick={async () => {
            toast.dismiss(t.id);
            try { await productsAPI.remove(p.id); toast.success("Deleted."); load(); }
            catch (err) { toast.error(err.message || "Failed."); }
          }} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}>Delete</button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity, style: { maxWidth: "330px" } });
  };

  return (
    <AdminLayout pageTitle="Products" pageSubtitle="What's on the shelf in the shop."
      permission="shop.view" feature="shop">
      {loading ? (
        <div className="arb__loading"><div className="acr__spinner" /></div>
      ) : (
        <>
          <div className="aap__stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(var(--admin-rust-rgb),0.1)" }}>
                  <HiOutlineShoppingBag style={{ color: "var(--admin-rust)", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">On sale</p>
              </div>
              <p className="aap__stat-value">{stats.live}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(22,163,74,0.1)" }}>
                  <HiOutlinePhoto style={{ color: "#16a34a", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">Items sold</p>
              </div>
              <p className="aap__stat-value">{stats.sold}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(217,119,6,0.1)" }}>
                  <HiOutlineShoppingBag style={{ color: "#d97706", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">Running low</p>
              </div>
              <p className="aap__stat-value">{stats.low}</p>
            </div>
          </div>

          <div className="awk__toolbar">
            <div className="ars__type-row">
              {[{ v: "", label: "All" }, ...STATUSES].map((s) => (
                <button key={s.label} type="button"
                  className={`ars__type-btn ${status === s.v ? "ars__type-btn--active" : ""}`}
                  onClick={() => setStatus(s.v)}>{s.label}</button>
              ))}
            </div>
            <input className="arb__form-input awk__search" value={q} placeholder="Search products…"
              onChange={(e) => setQ(e.target.value)} />
            {canEdit && (
              <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
                <HiOutlinePlus /> New product
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <div className="acard awk__empty-card">
              <HiOutlineShoppingBag />
              <p>{q ? "Nothing matches that search." : "No products yet."}</p>
              {canEdit && !q && (
                <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
                  <HiOutlinePlus /> Add the first one
                </button>
              )}
            </div>
          ) : (
            <div className="apr__list">
              {shown.map((p) => (
                <div className="acard apr__card" key={p.id}>
                  <div className="apr__card-img">
                    {p.images[0]
                      ? <img src={mediaUrl(p.images[0].path)} alt="" />
                      : <span className="apr__card-noimg"><HiOutlinePhoto /></span>}
                  </div>

                  <div className="apr__card-main">
                    <h3 className="apr__card-title">
                      {p.name}
                      <span className={`awk__status awk__status--${p.status}`}>{p.status}</span>
                    </h3>
                    {p.summary && <p className="apr__card-sum">{p.summary}</p>}
                    <p className="apr__card-meta">
                      {money(p.price)} · {p.variants.length} variant{p.variants.length === 1 ? "" : "s"}
                      {p.stock == null
                        ? " · stock not counted"
                        : p.stock === 0
                          ? <span className="apr__oos"> · sold out</span>
                          : ` · ${p.stock} in stock`}
                      {p.sold > 0 && ` · ${p.sold} sold`}
                    </p>
                  </div>

                  {canEdit && (
                    <div className="awk__card-actions">
                      <button type="button" className="awk__icon-btn" title="Edit" onClick={() => setEditing(p)}>
                        <HiOutlinePencilSquare />
                      </button>
                      <button type="button" className="awk__icon-btn awk__icon-btn--danger" title="Delete"
                        onClick={() => remove(p)}>
                        <HiOutlineTrash />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <ProductEditor
          product={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AdminLayout>
  );
}
